import { HsMatrix4 } from '../objects/matrix4.class';
import type { HsQuaternion } from '../objects/quaternion.class';
import { HsVector3 } from '../objects/vector3.class';
import type { HsHypersceneTypes } from '../types/hyperscene-types.type';

export class HsHypersceneMathUtils {
  static readonly #vector3Temp1: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3Temp2: HsVector3 = HsVector3.makeZeroVector();

  static readonly #vector3TempX: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3TempY: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3TempZ: HsVector3 = HsVector3.makeZeroVector();

  static readonly #matrix4Temp1: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  static readonly #matrix4Temp2: HsMatrix4 = HsMatrix4.makeIdentityMatrix();

  /**
   * Handles to euler angles.
   * @param quaternion quaternion.
   * @param eulerAngles euler angles.
   * @returns Result value.
   */
  public static convertToEulerAngles(quaternion: HsQuaternion, eulerAngles: HsVector3): HsVector3 {
    const zeroPosition: HsVector3 = HsHypersceneMathUtils.#vector3Temp1.set(0, 0, 0);
    const unitScale: HsVector3 = HsHypersceneMathUtils.#vector3Temp2.set(1, 1, 1);

    const matrix: HsMatrix4 = HsHypersceneMathUtils.#matrix4Temp1.compose(
      zeroPosition.values,
      quaternion.values,
      unitScale.values
    );

    const values: HsHypersceneTypes.Array16 = matrix.values;
    const m11: number = values[0];
    const m12: number = values[4];
    const m13: number = values[8];
    const m22: number = values[5];
    const m23: number = values[9];
    const m32: number = values[6];
    const m33: number = values[10];

    eulerAngles.values[1] = Math.asin(Math.max(-1, Math.min(1, m13)));

    if (Math.abs(m13) < 0.9999999) {
      eulerAngles.values[0] = Math.atan2(-m23, m33);
      eulerAngles.values[2] = Math.atan2(-m12, m11);
    } else {
      eulerAngles.values[0] = Math.atan2(m32, m22);
      eulerAngles.values[2] = 0;
    }

    return eulerAngles;
  }

  /**
   * Handles decompose matrix4.
   * @param matrix matrix.
   * @param position position.
   * @param quaternion quaternion.
   * @param scale scale.
   */
  public static decomposeMatrix4(
    matrix: HsMatrix4,
    position: HsVector3,
    quaternion: HsQuaternion,
    scale: HsVector3
  ): void {
    const values: HsHypersceneTypes.Array16 = matrix.values;

    position.set(values[12], values[13], values[14]);

    const determinant: number = matrix.determinant();
    const length: number = HsHypersceneMathUtils.#vector3Temp1.set(values[0], values[1], values[2]).length();

    const scaleX: number = determinant < 0 ? -length : length;
    const scaleY: number = HsHypersceneMathUtils.#vector3Temp1.set(values[4], values[5], values[6]).length();
    const scaleZ: number = HsHypersceneMathUtils.#vector3Temp1.set(values[8], values[9], values[10]).length();

    scale.set(scaleX, scaleY, scaleZ);

    const invertedScaleX: number = 1 / scaleX;
    const invertedScaleY: number = 1 / scaleY;
    const invertedScaleZ: number = 1 / scaleZ;

    this.#matrix4Temp2.copy(matrix);

    this.#matrix4Temp2.values[0] *= invertedScaleX;
    this.#matrix4Temp2.values[1] *= invertedScaleX;
    this.#matrix4Temp2.values[2] *= invertedScaleX;
    this.#matrix4Temp2.values[4] *= invertedScaleY;
    this.#matrix4Temp2.values[5] *= invertedScaleY;
    this.#matrix4Temp2.values[6] *= invertedScaleY;
    this.#matrix4Temp2.values[8] *= invertedScaleZ;
    this.#matrix4Temp2.values[9] *= invertedScaleZ;
    this.#matrix4Temp2.values[10] *= invertedScaleZ;

    quaternion.setFromRotationMatrix(this.#matrix4Temp2.values);
  }

  /**
   * Handles look at.
   * @param observerPoint observer point.
   * @param observablePoint observable point.
   * @param upDirection up direction.
   * @returns Result value.
   */
  public static lookAt(
    matrix: HsMatrix4,
    observerPoint: HsVector3,
    observablePoint: HsVector3,
    upDirection: HsVector3
  ): HsMatrix4 {
    this.#vector3TempX.set(0, 0, 0);
    this.#vector3TempY.set(0, 0, 0);
    this.#vector3TempZ.set(0, 0, 0);

    this.#vector3TempZ.subtractVectors(observerPoint, observablePoint);

    if (this.#vector3TempZ.length() === 0) {
      this.#vector3TempZ.values[2] = 1;
    }

    this.#vector3TempZ.normalize();
    this.#vector3TempX.crossVectors(upDirection, this.#vector3TempZ);

    if (this.#vector3TempX.length() === 0) {
      if (Math.abs(upDirection.z) === 1) {
        this.#vector3TempZ.values[0] += 0.0001;
      } else {
        this.#vector3TempZ.values[2] += 0.0001;
      }

      this.#vector3TempZ.normalize();
      this.#vector3TempX.crossVectors(upDirection, this.#vector3TempZ);
    }

    this.#vector3TempX.normalize();
    this.#vector3TempY.crossVectors(this.#vector3TempZ, this.#vector3TempX);

    const elements: HsHypersceneTypes.Array16 = matrix.values;

    elements[0] = this.#vector3TempX.x;
    elements[4] = this.#vector3TempY.x;
    elements[8] = this.#vector3TempZ.x;
    elements[1] = this.#vector3TempX.y;
    elements[5] = this.#vector3TempY.y;
    elements[9] = this.#vector3TempZ.y;
    elements[2] = this.#vector3TempX.z;
    elements[6] = this.#vector3TempY.z;
    elements[10] = this.#vector3TempZ.z;

    return matrix;
  }

  /**
   * Handles random from seed.
   * @param seed seed.
   * @returns Result value.
   */
  public static randomFromSeed(seed: number): number {
    let hash: number = (seed += 0x6d2b79f5);
    hash = Math.imul(hash ^ (hash >>> 15), hash | 1);
    hash ^= hash + Math.imul(hash ^ (hash >>> 7), hash | 61);
    return ((hash ^ (hash >>> 14)) >>> 0) / 4294967296;
  }
}
